import { useState, useRef, useCallback } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { PageHeader, PageShell } from '../../components/ui/PageHeader';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { Plus } from 'lucide-react';
import { Alert } from '../../components/ui/Alert';
import { ImageCropper } from '../../components/ui/ImageCropper';
import { useAuth } from '../../hooks/useAuth';
import {
  useSignatures,
  useCreateSignature,
  useUpdateSignature,
  useDeleteSignature,
  useUploadSignaturePhoto,
} from '../../hooks/useSignatures';
import type { Signature, CreateSignatureData } from '../../api/signatures';

export function Signatures() {
  const { user: currentUser } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState<Signature | null>(null);

  const { data, isLoading, error } = useSignatures();
  const createSignature = useCreateSignature();
  const updateSignature = useUpdateSignature();
  const deleteSignature = useDeleteSignature();
  const uploadPhoto = useUploadSignaturePhoto();

  const isAdmin = currentUser?.role === 'admin';

  if (!isAdmin) {
    return (
      <div className="p-8">
        <Alert variant="error">You don't have permission to access this page.</Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <PageShell>
        <LoadingState />
      </PageShell>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Alert variant="error">Failed to load signatures: {error.message}</Alert>
      </div>
    );
  }

  const signatures = data?.signatures || [];
  const activeSignatures = signatures.filter((s) => s.isActive);

  const handleCreate = (data: CreateSignatureData, pendingPhoto: Blob | null) => {
    createSignature.mutate(data, {
      onSuccess: (result) => {
        if (pendingPhoto) {
          const file = new File([pendingPhoto], 'photo.jpg', { type: 'image/jpeg' });
          uploadPhoto.mutate(
            { id: result.signature.id, file },
            { onSuccess: () => setShowCreateModal(false) }
          );
        } else {
          setShowCreateModal(false);
        }
      },
    });
  };

  const handleUpdate = (id: string, data: CreateSignatureData, pendingPhoto: Blob | null) => {
    updateSignature.mutate(
      { id, data },
      {
        onSuccess: () => {
          if (pendingPhoto) {
            const file = new File([pendingPhoto], 'photo.jpg', { type: 'image/jpeg' });
            uploadPhoto.mutate(
              { id, file },
              { onSuccess: () => setSelectedSignature(null) }
            );
          } else {
            setSelectedSignature(null);
          }
        },
      }
    );
  };

  return (
    <PageShell>
      <PageHeader eyebrow="Admin"
        title="Signatures"
        subtitle="Email signatures for team members."
        meta={signatures.length > 0 ? <span>{activeSignatures.length} active</span> : undefined}
        actions={
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowCreateModal(true)}>
            New signature
          </Button>
        }
      />

      <Card>
        {signatures.length === 0 ? (
          <EmptyState title="No signatures yet" description="Create one with New signature." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-light">
                  <th className="text-left py-3 px-4 text-body-xs font-medium text-text-tertiary">Name</th>
                  <th className="text-left py-3 px-4 text-body-xs font-medium text-text-tertiary">Title</th>
                  <th className="text-left py-3 px-4 text-body-xs font-medium text-text-tertiary">Email</th>
                  <th className="text-right py-3 px-4 text-body-xs font-medium text-text-tertiary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {signatures.map((sig) => (
                  <tr key={sig.id} className="border-b border-border-light last:border-0 hover:bg-surface-secondary">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        {sig.photoUrl ? (
                          <img
                            src={sig.photoUrl}
                            alt={sig.fullName}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-surface-secondary flex items-center justify-center text-text-tertiary text-body-xs font-medium">
                            {sig.fullName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                          </div>
                        )}
                        <span className="text-body-sm font-medium text-text-primary">{sig.fullName}</span>
                        {/* Active is the default; only the exception gets a pill */}
                        {!sig.isActive && <Badge size="sm">Inactive</Badge>}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-body-sm text-text-secondary">{sig.title}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-body-xs text-text-tertiary">{sig.email}</span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            window.open(`/api/signatures/${sig.id}/render`, '_blank');
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/api/signatures/${sig.id}/render`);
                          }}
                        >
                          Copy Link
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedSignature(sig)}
                        >
                          Edit
                        </Button>
                        {sig.isActive && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm('Deactivate this signature?')) {
                                deleteSignature.mutate(sig.id);
                              }
                            }}
                          >
                            Deactivate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <SignatureFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreate}
        isSaving={createSignature.isPending || uploadPhoto.isPending}
        title="Create Signature"
      />

      {selectedSignature && (
        <SignatureFormModal
          isOpen={true}
          onClose={() => setSelectedSignature(null)}
          onSave={(data, pendingPhoto) => handleUpdate(selectedSignature.id, data, pendingPhoto)}
          isSaving={updateSignature.isPending || uploadPhoto.isPending}
          initialData={selectedSignature}
          title="Edit Signature"
        />
      )}
    </PageShell>
  );
}

function SignatureFormModal({
  isOpen,
  onClose,
  onSave,
  isSaving,
  initialData,
  title,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateSignatureData, pendingPhoto: Blob | null) => void;
  isSaving: boolean;
  initialData?: Signature;
  title: string;
}) {
  const [fullName, setFullName] = useState(initialData?.fullName || '');
  const [sigTitle, setSigTitle] = useState(initialData?.title || '');
  const [email, setEmail] = useState(initialData?.email || '');
  const [phone, setPhone] = useState(initialData?.phone || '');

  // Photo state
  const [pendingPhotoBlob, setPendingPhotoBlob] = useState<Blob | null>(null);
  const [pendingPhotoPreview, setPendingPhotoPreview] = useState<string | null>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentPhotoUrl = pendingPhotoPreview || initialData?.photoUrl || null;

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Reset file input so the same file can be re-selected
    e.target.value = '';
  }, []);

  const handleCropComplete = useCallback((croppedBlob: Blob) => {
    setPendingPhotoBlob(croppedBlob);
    setPendingPhotoPreview(URL.createObjectURL(croppedBlob));
    setCropImageSrc(null);
  }, []);

  const handleCropCancel = useCallback(() => {
    setCropImageSrc(null);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(
      {
        fullName,
        title: sigTitle,
        email,
        phone: phone || undefined,
      },
      pendingPhotoBlob,
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Photo upload section */}
          <div>
            <label className="text-body-sm font-medium text-text-primary block mb-2">
              Profile Photo
            </label>
            <div className="flex items-center gap-4">
              {currentPhotoUrl ? (
                <img
                  src={currentPhotoUrl}
                  alt={fullName || 'Photo preview'}
                  className="h-16 w-16 rounded object-cover border border-border-light"
                />
              ) : (
                <div className="h-16 w-16 rounded bg-surface-secondary flex items-center justify-center text-text-tertiary text-body-sm font-medium border border-border-light">
                  {fullName ? fullName.split(' ').map((n) => n[0]).join('').slice(0, 2) : '?'}
                </div>
              )}
              <div className="flex flex-col gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {currentPhotoUrl ? 'Change Photo' : 'Upload Photo'}
                </Button>
                <span className="text-body-xs text-text-tertiary">
                  Select an image to crop and position
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Walter Grandjean"
              required
            />
            <Input
              label="Title"
              value={sigTitle}
              onChange={(e) => setSigTitle(e.target.value)}
              placeholder="Founder Associate"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="walter@sequesto.com"
              required
            />
            <Input
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+32 478 485 880"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!fullName || !sigTitle || !email || isSaving}>
              {isSaving ? 'Saving...' : initialData ? 'Save Changes' : 'Create Signature'}
            </Button>
          </div>
        </form>
      </Modal>

      {cropImageSrc && (
        <ImageCropper
          imageSrc={cropImageSrc}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1}
          cropShape="rect"
        />
      )}
    </>
  );
}
